import {
  Form,
  Input,
  Select,
  Checkbox,
  Radio,
  Button,
  Tooltip,
  InputNumber,
  Space,
  DatePicker,
  Cascader,
  TimePicker,
  TreeSelect,
  Modal,
  Table,
  Badge,
} from 'antd'
import { useMemo } from 'react'
import { SvgIcon, BasSwitch, SignTitle, FilterFormSelect } from '@/components'
import { Message } from '@/components/message'
import { disabledDate, disabledDateTime, monthList, weekList } from '@/utils/appointment'
import { displayRender, deviceFilter, extractLabelText } from './utils/deviceUtils'

import { CaseDetail } from './caseDetail'
import { Container, ConfigContainer, TemplateContainer } from './styled'
import {
  priorityOptions,
  protocolOptions,
  conditionOptions,
  schedulerTypeOptions,
  executeTypeOptions,
  paginationConfig,
  validationRules,
} from './consts'
import { useCreateTaskMain } from './hooks'
import { DefaultOptionType } from 'antd/es/select'
import { t } from '@/i18n'

// 步骤指示器组件
const StepIndicator = ({ step }: { step: number }) => (
  <div className="steps-container">
    <div className="steps">
      <div className="step-number active number-1">01</div>
      <div className={step === 1 ? 'step-number number-2' : 'step-number number-2 active'}>02</div>
      <div className="step-wrapper-container">
        <div className="step-wrapper">
          <div className="step-content active">
            <div className="step-icon">
              <SvgIcon width="40" height="40" name={t('任务配置')} />
            </div>
            <span className="step-text">{t('任务配置')}</span>
          </div>
        </div>
        <div className="step-arrow">
          <SvgIcon
            width="24"
            height="28"
            name={step === 1 ? 'step-arrow-normal' : 'step-arrow-active'}
          />
        </div>
        <div className="step-wrapper">
          <div className="step-content">
            <div className="step-icon">
              <SvgIcon
                width="40"
                height="40"
                name={step === 1 ? t('模版及用例选择灰') : t('模版及用例选择')}
              />
            </div>
            <span className="step-text" style={{ color: step === 1 ? '#999' : '#333' }}>
              {t('模板及用例选择')}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
)

// 底部按钮组件
const FooterButtons = ({
  step,
  handleBack,
  submitHandler,
  createForm,
  selectedCaseIds,
  createTaskLoading,
  formState,
}: any) => (
  <div className="footer">
    {step === 2 && (
      <Button type="default" onClick={handleBack}>
        {t('上一步')}
      </Button>
    )}
    <Button
      type="primary"
      onClick={() => {
        if (step === 1) {
          // 验证周期任务
          if (formState.executeType === 3) {
            // 验证每周任务是否选择了星期
            if (formState.schedulerType === 'scheduler_type_weekly') {
              if (!formState.dayOfWeek || formState.dayOfWeek.length === 0) {
                Message.error(t('每周任务请选择星期'))
                return
              }
              if (!formState.taskTime) {
                Message.error(t('每周任务请选择执行时间'))
                return
              }
            }
            // 验证每月任务是否选择了日期
            else if (formState.schedulerType === 'scheduler_type_monthly') {
              if (!formState.dayOfMonth || formState.dayOfMonth.length === 0) {
                Message.error(t('每月任务请选择日期'))
                return
              }
              if (!formState.taskTime) {
                Message.error(t('每月任务请选择执行时间'))
                return
              }
            }
            // 验证每日任务是否选择了执行时间
            else if (formState.schedulerType === 'scheduler_type_daily') {
              if (!formState.taskTime) {
                Message.error(t('每日任务请选择执行时间'))
                return
              }
            }
          }
          if (createForm) {
            createForm.submit()
          } else {
            console.error(t('表单实例不存在'))
          }
        } else {
          submitHandler()
        }
      }}
      disabled={step === 2 && selectedCaseIds.length === 0}
      loading={createTaskLoading}
    >
      {step === 1 ? t('下一步') : t('创建任务')}
    </Button>
  </div>
)

export const CreateTask = () => {
  const data = useCreateTaskMain()
  const {
    formState,
    setFormState,
    createForm,
    form,
    handleFormValueChange,
    step,
    handleBack,
    cascaderDeviceListData,
    fetchSecurityDeviceList,
    templateValue,
    setTemplateValue,
    templateData,
    searchTemplateParams,
    selectedTemplateIds,
    templateIds,
    caseValue,
    setCaseValue,
    caseData,
    searchCaseParams,
    selectedCaseIds,
    caseKey,
    saveTemplateModal,
    setSaveTemplateModal,
    detailVisible,
    setDetailVisible,
    taskGroup,
    emailList,
    agentList,
    attackTypeList,
    createTaskLoading,
    saveCaseToTemplate,
    saveCaseToTemplateLoading,
    templateLoading,
    caseLoading,
    options,
    templateSearchOptions,
    onExecuteTypeChange,
    onExecuteTemplateLatestCaseChange,
    onCaseTableChange,
    onTemplateTableChange,
    onCaseSelectAll,
    onSelectAll,
    isAllSelected,
    isIndeterminate,
    submitHandler,
    handleSubmit,
    onSaveTemplateSubmit,
    templateRowSelection,
    templateColumns,
    selectedCaseColumns,
    caseRowSelection,
  } = data
  const cascaderAgentListData = useMemo(
    () =>
      agentList
        .map(domain => {
          // 过滤出有IP的agents
          const agentsWithIP = domain.os
            .flatMap(os => os.agents)
            .filter(agent => agent.ip && agent.ip.length > 0)

          // 统计有IP的agent总数
          const totalAgentsWithIP = agentsWithIP.length

          // 如果没有有IP的agent，则不显示该安全域
          if (totalAgentsWithIP === 0) {
            return null
          }

          return {
            value: domain.id,
            label: (
              <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{domain.name}</span>
                {totalAgentsWithIP > 0 && (
                  <span style={{ color: '#4238C9', marginLeft: '8px' }}>({totalAgentsWithIP})</span>
                )}
              </span>
            ),

            children: agentsWithIP
              .sort((a, b) => {
                // 在线的排在前面
                if (a.aliveStatus === 'AliveStatusTrue' && b.aliveStatus !== 'AliveStatusTrue') {
                  return -1
                }
                if (a.aliveStatus !== 'AliveStatusTrue' && b.aliveStatus === 'AliveStatusTrue') {
                  return 1
                }
                return 0
              })
              .map(agent => ({
                value: agent.agentId,
                disabled: agent.aliveStatus !== 'AliveStatusTrue',
                label: (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span>{agent.agentName}</span>
                    <span style={{ flexShrink: 0 }}>
                      <Badge
                        style={{ marginLeft: 10 }}
                        status={agent.aliveStatus === 'AliveStatusTrue' ? 'success' : 'default'}
                      />
                    </span>
                  </span>
                ),

                agent: agent,
                ip: agent.ip[0], // 保留第一个IP用于数据传递
              })),
          }
        })
        .filter(Boolean), // 过滤掉null值
    [agentList],
  )

  // 自定义Agent显示渲染函数
  const agentDisplayRender = (labels: string[], selectedOptions?: DefaultOptionType[]) => {
    if (!selectedOptions || selectedOptions.length === 0) return ''

    // 只取第一个选择项
    const firstOption = selectedOptions[0]

    if (firstOption?.agent) {
      // 获取安全域名称
      const domainName =
        agentList.find(domain =>
          domain.os.some(os =>
            os.agents.some(agent => agent.agentId === firstOption.agent.agentId),
          ),
        )?.name || ''

      const agentName = firstOption.agent.agentName
      const ip = firstOption?.ip // IP值

      if (ip) {
        // 显示格式：安全域名称-Agent名称-IP
        return `${domainName}-${agentName}-${ip}`
      } else {
        // 显示格式：安全域名称-Agent名称
        return `${domainName}-${agentName}`
      }
    }

    // 如果没有找到agent信息，尝试从labels中获取信息，并用 extractLabelText 处理
    if (labels && labels.length > 0) {
      // 用 extractLabelText 提取文本
      const textLabels = labels.map(extractLabelText)
      if (textLabels.length === 2) {
        return `${textLabels[0]}-${textLabels[1]}`
      } else if (textLabels.length === 1) {
        return textLabels[0]
      }
    }

    return ''
  }

  const agentFilter = (inputValue: string, path: DefaultOptionType[]) => {
    const searchValue = inputValue.toLowerCase()
    return path.some(option => {
      const labelText = extractLabelText(option.label).toLowerCase()
      return labelText.includes(searchValue)
    })
  }

  const siteSearchRender = (inputValue: string, path: DefaultOptionType[]) => {
    if (path.length === 0) return null
    const searchValue = inputValue.toLowerCase()
    return (
      <span>
        {path.map((option, idx) => {
          const labelText = extractLabelText(option.label)
          const lowerLabel = labelText.toLowerCase()
          const matchIdx = lowerLabel.indexOf(searchValue)
          if (matchIdx !== -1 && searchValue) {
            // 高亮匹配部分
            return (
              <span key={idx}>
                {labelText.slice(0, matchIdx)}
                <span style={{ color: '#4238c9', fontWeight: 600 }}>
                  {labelText.slice(matchIdx, matchIdx + searchValue.length)}
                </span>
                {labelText.slice(matchIdx + searchValue.length)}
                {idx < path.length - 1 ? ' / ' : ''}
              </span>
            )
          }
          return (
            <span key={idx}>
              {labelText}
              {idx < path.length - 1 ? ' / ' : ''}
            </span>
          )
        })}
      </span>
    )
  }

  return (
    <Container>
      <div className="title">{t('创建任务')}</div>
      <StepIndicator step={step} />
      {/* 任务配置 */}
      {step === 1 && (
        <ConfigContainer>
          <Form
            id="waf-create-config-form"
            onFinish={submitHandler}
            form={createForm}
            layout="horizontal"
            colon={false}
            labelWrap
            labelCol={{ flex: '75px', style: { textAlign: 'right' } }}
            onValuesChange={handleFormValueChange}
            initialValues={{
              name: formState.name,
              verification_mode_id: formState.verifyMode,
              executeType: formState.executeType,
              executionTimes: formState.executionTimes,
              priority: formState.priority,
              executeTemplateLatestCase: formState.executeTemplateLatestCase,
              speed: formState.speed,
              taskTime: formState.taskTime,
              deviceIds: formState.deviceIds,
              filter: formState.filter,
              send: formState.send,
              condition: formState.condition,
              protocol: formState.protocol,
              account: formState.account,
              password: formState.password,
              proxy: formState.proxy,
              emailList: formState.emailList,
              agentId: formState.agentId,
              hostId: '1',
              dnsSinkhole: formState.dnsSinkhole,
              timeout: formState.timeout,
            }}
          >
            {/* 任务基本信息 */}
            <div className="section">
              <div className="section-title">
                {t('任务基本信息')}
                <div className="section-bar" />
              </div>
              <div className="form-content">
                <Form.Item
                  label={t('任务名称')}
                  name="name"
                  required
                  rules={validationRules.taskName}
                  className="form-item required"
                >
                  <Input placeholder={t('请输入')} style={{ width: '300px' }} allowClear />
                </Form.Item>

                <Form.Item label={t('任务分组')} name="groupId" className="form-item">
                  <Select placeholder={t('请选择')} style={{ width: 300 }} allowClear>
                    {taskGroup?.map(item => (
                      <Select.Option key={item.groupId} value={item.groupId}>
                        {item.groupName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>

            {/* 任务验证配置 */}
            <div className="section">
              <div className="section-title">
                {t('任务验证配置')}
                <div className="section-bar" />
              </div>
              <div className="form-content">
                <Form.Item
                  label={t('验证模式')}
                  name="verification_mode_id"
                  className="form-item"
                  rules={validationRules.verificationMode}
                  // style={{ marginLeft: 35 }}
                >
                  <Checkbox.Group
                    disabled
                    onChange={e => {
                      setFormState(prev => ({ ...prev, verifyMode: e as number[] }))
                      createForm.setFieldsValue({
                        verification_mode_id: e as number[],
                        agentId: '',
                        deviceIds: '',
                      })
                    }}
                    value={[2]}
                    style={{ marginLeft: 10 }}
                  >
                    <Space>
                      <Checkbox value={2}>
                        Agent
                        <span style={{ color: '#A6A3D6' }}> → </span>
                        {t('外部情报')}
                      </Checkbox>
                    </Space>
                  </Checkbox.Group>
                </Form.Item>

                {formState.verifyMode.includes(2) && (
                  <div className="verify-config">
                    <div className="config-title-box">
                      <div className="config-title">
                        <span style={{ color: '#86909C', marginRight: 8 }}>{t('模式配置')}</span>
                        <span style={{ color: '#86909C' }}>Agent</span>
                        <span style={{ color: '#A6A3D6' }}> → </span>
                        <span style={{ color: '#86909C' }}>{t('外部情报')}</span>
                      </div>
                    </div>
                    <div className="config-form">
                      <Form.Item
                        label={
                          <span style={{ color: '#333', fontWeight: 400 }}>{t('攻击主体')}</span>
                        }
                        required
                        className="attack-subject-form-item"
                      >
                        <div className="flex-row">
                          <div className="label">Agent</div>
                          <Form.Item
                            name="agentId"
                            noStyle
                            rules={[{ required: true, message: t('请选择Agent') }]}
                          >
                            <Cascader
                              placeholder="Agent"
                              options={cascaderAgentListData}
                              showArrow
                              maxTagCount="responsive"
                              displayRender={agentDisplayRender}
                              showSearch={{
                                filter: agentFilter,
                                matchInputWidth: true,
                                render: siteSearchRender,
                                limit: 50,
                              }}
                              dropdownMenuColumnStyle={{ maxWidth: 520 }}
                              showCheckedStrategy={Cascader.SHOW_CHILD}
                              value={createForm.getFieldValue('agentId') || []}
                              onChange={value => {
                                console.log('value', value)
                                // value 可能是二维数组或三级数组
                                const arr =
                                  value && value.length > 0 && Array.isArray(value[0])
                                    ? value
                                    : [value]
                                createForm.setFieldsValue({ srcAgentId: arr })
                                // 攻击主体变更时清空目标主体和安全设备
                                createForm.setFieldsValue({ dstAgentId: [], device_ids: [] })

                                // 当选择agent时，获取对应的安全设备列表
                                if (value && value.length > 0) {
                                  // 获取选中的agentId（可能是第二级或第三级）
                                  const agentId = value.length >= 2 ? value[1] : value[0]
                                  if (agentId) {
                                    fetchSecurityDeviceList(String(agentId))
                                  }
                                } else {
                                  // 清空选择时，清空安全设备列表
                                  fetchSecurityDeviceList('')
                                }
                              }}
                            />
                          </Form.Item>
                          <Button
                            type="link"
                            onClick={() => {
                              // 添加攻击机的逻辑
                              window.open('/configure/client/list', '_blank')
                            }}
                          >
                            {t('添加')}
                          </Button>
                        </div>
                      </Form.Item>

                      <Form.Item
                        label={
                          <span style={{ color: '#333', fontWeight: 400, marginLeft: 10 }}>
                            {t('目标主体')}
                          </span>
                        }
                        className="target-subject-form-item"
                      >
                        <div className="flex-row">
                          <div className="label">{t('外部情报')}</div>
                          <Form.Item noStyle name="hostId">
                            <Select disabled>
                              <Select.Option value="1">{t('外部情报内容')}</Select.Option>
                            </Select>
                          </Form.Item>
                        </div>
                      </Form.Item>

                      <Form.Item
                        label={<span style={{ color: '#333', fontWeight: 400, marginLeft: 10 }} />}
                        className="target-subject-form-item"
                      >
                        <div className="flex-row">
                          <div className="label">DNS Sinkhole</div>
                          <Form.Item noStyle name="dnsSinkhole">
                            <Input
                              placeholder={t('请输入')}
                              style={{ width: '300px' }}
                              allowClear
                            />
                          </Form.Item>
                        </div>
                      </Form.Item>

                      <Form.Item
                        label={
                          <span style={{ color: '#333', fontWeight: 400 }}>{t('安全设备')}</span>
                        }
                        className="security-device-form-item"
                      >
                        <div className="flex-row">
                          <Form.Item noStyle name="deviceIds">
                            <Cascader
                              placeholder={t('安全设备')}
                              options={cascaderDeviceListData}
                              showArrow
                              displayRender={displayRender}
                              value={formState.deviceIds}
                              showSearch={{
                                filter: deviceFilter,
                                matchInputWidth: true,
                                render: (inputValue, path) => {
                                  if (path.length === 0) return inputValue
                                  const searchValue = inputValue.toLowerCase()
                                  return (
                                    <span>
                                      {path.map((option, idx) => {
                                        const labelText = extractLabelText(option.label)
                                        const lowerLabel = labelText.toLowerCase()
                                        const matchIdx = lowerLabel.indexOf(searchValue)
                                        if (matchIdx !== -1 && searchValue) {
                                          return (
                                            <span key={idx}>
                                              {labelText.slice(0, matchIdx)}
                                              <span style={{ color: '#4238c9', fontWeight: 600 }}>
                                                {labelText.slice(
                                                  matchIdx,
                                                  matchIdx + searchValue.length,
                                                )}
                                              </span>
                                              {labelText.slice(matchIdx + searchValue.length)}
                                              {idx < path.length - 1 ? ' / ' : ''}
                                            </span>
                                          )
                                        }
                                        return (
                                          <span key={idx}>
                                            {labelText}
                                            {idx < path.length - 1 ? ' / ' : ''}
                                          </span>
                                        )
                                      })}
                                    </span>
                                  )
                                },
                                limit: 50,
                              }}
                              dropdownMenuColumnStyle={{ maxWidth: 520 }}
                              onChange={value => {
                                console.log('安全设备选择变化:', value)
                                setFormState(prev => ({ ...prev, deviceIds: value as any }))
                                createForm.setFieldsValue({ deviceIds: value })
                              }}
                              onFocus={() => {
                                console.log('安全设备级联框获得焦点，当前值:', formState.deviceIds)
                                console.log('级联框选项数据:', cascaderDeviceListData)
                                console.log('级联框选项数据长度:', cascaderDeviceListData.length)
                              }}
                              onDropdownVisibleChange={open => {
                                console.log('安全设备级联框下拉状态变化:', open)
                                if (open) {
                                  console.log('级联框选项数据:', cascaderDeviceListData)
                                  console.log('当前表单值:', createForm.getFieldValue('deviceIds'))
                                }
                              }}
                            />
                          </Form.Item>
                          <Button
                            type="link"
                            onClick={() => {
                              window.open('/configure/device/access-management', '_blank')
                            }}
                          >
                            {t('添加接入设备')}
                          </Button>
                        </div>
                        {/* 移除链路选择提示，现在是两级结构 */}
                      </Form.Item>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 任务执行配置 */}
            <div className="section">
              <div className="section-title">
                {t('任务执行配置')}
                <div className="section-bar" />
              </div>
              <div className="form-content">
                <Form.Item
                  label={t('执行类型')}
                  name="executeType"
                  required
                  className="form-item radio-box"
                  // style={{ marginLeft: 35 }}
                >
                  <Radio.Group
                    // style={{
                    //   marginLeft: 8,
                    // }}
                    onChange={e => {
                      onExecuteTypeChange(e.target.value)
                    }}
                    value={formState.executeType}
                  >
                    {executeTypeOptions.map(option => (
                      <Radio.Button key={option.value} value={option.value}>
                        {option.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <div>
                  {formState.executeType === 2 && (
                    <div className="execute-config">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 24,
                          marginLeft: 115,
                        }}
                      >
                        <Form.Item
                          name="taskTime"
                          label={
                            <span style={{ fontWeight: 400, color: '#666' }}>
                              {t('执行开始时间')}
                            </span>
                          }
                          rules={[{ required: true, message: t('请选择执行开始时间') }]}
                        >
                          <DatePicker
                            placeholder={t('请选择日期与时间')}
                            style={{
                              width: '236px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            showTime
                            value={formState.taskTime}
                            onChange={value => {
                              setFormState(prev => ({ ...prev, taskTime: value as any }))
                            }}
                            disabledDate={disabledDate}
                            disabledTime={disabledDateTime}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {formState.executeType === 3 && (
                    <div className="execute-config">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 24,
                          marginLeft: 115,
                        }}
                      >
                        <span
                          style={{
                            marginRight: 12,
                            fontWeight: 400,
                            color: '#666',
                            marginLeft: 10,
                          }}
                        >
                          {t('周期配置')}
                        </span>
                        <div>
                          <Input.Group compact style={{ display: 'flex' }}>
                            <Form.Item name="schedulerType" noStyle>
                              <Select
                                style={{ width: '98px' }}
                                value={formState.schedulerType}
                                onChange={e => {
                                  // 保存当前时间值
                                  const currentTime = formState.taskTime
                                  setFormState(prev => ({
                                    ...prev,
                                    schedulerType: e,
                                    taskTime: currentTime,
                                  }))
                                  // 确保表单也保留时间值
                                  if (currentTime) {
                                    createForm.setFieldsValue({ taskTime: currentTime })
                                  }
                                }}
                              >
                                {schedulerTypeOptions.map(option => (
                                  <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            {formState.schedulerType === 'scheduler_type_daily' && (
                              <div
                                style={{
                                  display: 'flex',
                                  borderRadius: '0 6px 6px 0',
                                  marginRight: 10,
                                  alignItems: 'center',
                                }}
                              >
                                <Form.Item
                                  name="taskTime"
                                  noStyle
                                  rules={[{ required: true, message: t('请选择执行开始时间') }]}
                                >
                                  <TimePicker
                                    style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                    value={formState.taskTime}
                                    format="HH:mm:ss"
                                    onChange={value => {
                                      if (value && value.isValid()) {
                                        setFormState(prev => ({ ...prev, timeOfDay: value }))
                                        createForm.setFieldsValue({ taskTime: value })
                                      } else {
                                        setFormState(prev => ({ ...prev, taskTime: null }))
                                        createForm.setFieldsValue({
                                          taskTime: null,
                                        })
                                      }
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            )}
                            {formState.schedulerType === 'scheduler_type_weekly' && (
                              <Input.Group compact>
                                <div className="date-select">
                                  <Form.Item
                                    name="dayOfWeek"
                                    noStyle
                                    rules={[{ required: true, message: t('请选择星期') }]}
                                  >
                                    <TreeSelect
                                      showSearch={false}
                                      treeData={weekList}
                                      treeCheckable
                                      maxTagCount={1}
                                      showCheckedStrategy="SHOW_PARENT"
                                      style={{
                                        width: '120px',
                                        borderRadius: 'none',
                                        textAlign: 'left',
                                      }}
                                      placeholder={t('请选择星期')}
                                      value={formState.dayOfWeek}
                                      onChange={week => {
                                        setFormState(prev => ({
                                          ...prev,
                                          dayOfWeek: week || [],
                                        }))
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                                <Form.Item
                                  name="taskTime"
                                  noStyle
                                  rules={[{ required: true, message: t('请选择执行开始时间') }]}
                                >
                                  <TimePicker
                                    style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                    value={formState.taskTime}
                                    format="HH:mm:ss"
                                    onChange={value => {
                                      if (value && value.isValid()) {
                                        setFormState(prev => ({ ...prev, taskTime: value }))
                                        createForm.setFieldsValue({ taskTime: value })
                                      } else {
                                        setFormState(prev => ({ ...prev, taskTime: null }))
                                        createForm.setFieldsValue({
                                          taskTime: null,
                                        })
                                      }
                                    }}
                                  />
                                </Form.Item>
                              </Input.Group>
                            )}
                            {formState.schedulerType === 'scheduler_type_monthly' && (
                              <Input.Group compact>
                                <div className="date-select" style={{ borderRadius: 'none' }}>
                                  <Form.Item
                                    name="dayOfMonth"
                                    noStyle
                                    rules={[{ required: true, message: t('请选择日期') }]}
                                  >
                                    <TreeSelect
                                      treeData={monthList()}
                                      treeCheckable
                                      key="value"
                                      showCheckedStrategy="SHOW_PARENT"
                                      style={{ width: '120px' }}
                                      maxTagCount={1}
                                      value={formState.dayOfMonth}
                                      onChange={(e: any) => {
                                        setFormState(prev => ({
                                          ...prev,
                                          dayOfMonth: e,
                                        }))
                                      }}
                                      placeholder={t('选择日期')}
                                    />
                                  </Form.Item>
                                </div>
                                <Form.Item
                                  name="taskTime"
                                  noStyle
                                  rules={[{ required: true, message: t('请选择执行开始时间') }]}
                                >
                                  <TimePicker
                                    style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                    value={formState.taskTime}
                                    format="HH:mm:ss"
                                    onChange={value => {
                                      if (value && value.isValid()) {
                                        setFormState(prev => ({ ...prev, taskTime: value }))
                                        createForm.setFieldsValue({ taskTime: value })
                                      } else {
                                        setFormState(prev => ({ ...prev, taskTime: null }))
                                        createForm.setFieldsValue({
                                          taskTime: null,
                                        })
                                      }
                                    }}
                                  />
                                </Form.Item>
                              </Input.Group>
                            )}
                            <div style={{ width: 230 }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ width: 50, fontWeight: 400, color: '#666' }}>
                                  {t('总执行')}
                                </div>
                                <Form.Item name="executionTimes" noStyle>
                                  <InputNumber
                                    style={{ margin: '0 4px', width: '150px' }}
                                    placeholder={t('默认不限次数')}
                                    min={0}
                                    max={999}
                                    value={formState.executionTimes}
                                    onChange={val => {
                                      setFormState(prev => ({
                                        ...prev,
                                        executionTimes: val || 0,
                                      }))
                                    }}
                                  />
                                </Form.Item>
                                <span>{t('次')}</span>
                              </div>
                            </div>
                          </Input.Group>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="execute-config">
                  <Form.Item label={t('执行速率')} name="speed" className="form-item">
                    <InputNumber
                      placeholder={t('请选择')}
                      style={{ width: 300, marginRight: 16 }}
                      min={1}
                      max={99}
                      value={formState.speed}
                      onChange={value => {
                        setFormState(prev => ({ ...prev, speed: value || 10 }))
                        createForm.setFieldsValue({ speed: value })
                      }}
                      addonAfter={<span>{t('个/秒')}</span>}
                    />

                    <Tooltip title={t('可配区间1-99')}>
                      <SvgIcon
                        name="提示"
                        style={{ width: '14px', height: '14px', marginTop: 6 }}
                      />
                    </Tooltip>
                  </Form.Item>
                  <Form.Item label={t('超时时间')} name="timeout" className="form-item">
                    <InputNumber
                      placeholder={t('请选择')}
                      style={{ width: 300, marginRight: 16 }}
                      value={formState.timeout}
                      min={5}
                      max={30}
                      onChange={value => {
                        setFormState(prev => ({ ...prev, timeout: value || 5 }))
                        createForm.setFieldsValue({ timeout: value })
                      }}
                      addonAfter={<span>{t('秒')}</span>}
                    />

                    <Tooltip title={t('可配区间：5-30 秒，情报请求超时时间')}>
                      <SvgIcon
                        name="提示"
                        style={{ width: '14px', height: '14px', marginTop: 6 }}
                      />
                    </Tooltip>
                  </Form.Item>
                  <Form.Item
                    label={
                      <div className="custom-label">
                        <span>{t('优先级')}</span>
                      </div>
                    }
                    name="priority"
                    className="form-item"
                  >
                    <Select
                      placeholder={t('请选择')}
                      style={{ width: 300, marginRight: 16 }}
                      value={formState.priority}
                      onChange={value => {
                        setFormState(prev => ({ ...prev, priority: value }))
                        createForm.setFieldsValue({ priority: value })
                      }}
                    >
                      {priorityOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                    <Tooltip title={t('数字越大，优先级越高')}>
                      <SvgIcon
                        name="提示"
                        style={{ width: '14px', height: '14px', marginTop: 2 }}
                      />
                    </Tooltip>
                  </Form.Item>
                  {formState.executeType === 3 && (
                    <Form.Item
                      label={t('执行增量')}
                      name="executeTemplateLatestCase"
                      className="execute-template-latest-case form-item"
                    >
                      <Form.Item name="executeTemplateLatestCase" noStyle>
                        <BasSwitch
                          checked={formState.executeTemplateLatestCase}
                          onChange={onExecuteTemplateLatestCaseChange}
                        />
                      </Form.Item>
                      <Tooltip
                        title={t('启用后则对全选用例的模版周期执行时执行新添加到模版中的用例')}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <SvgIcon
                          name="提示"
                          style={{ width: '14px', height: '14px', marginTop: 2, marginLeft: 10 }}
                        />
                      </Tooltip>
                    </Form.Item>
                  )}
                </div>
              </div>
            </div>

            {/* 任务高级配置 */}
            <div className="section">
              <div className="section-title">
                {t('任务高级配置')}
                <div className="section-bar" />
              </div>
              <div className="form-content">
                <div className="code" style={{ display: 'flex' }}>
                  <Form.Item
                    label={t('代理')}
                    name="proxy"
                    className="form-item"
                    rules={
                      formState.enableAuth
                        ? [{ required: true, message: t('启用账密认证时，代理池为必填项') }]
                        : []
                    }
                  >
                    <Input
                      addonBefore={
                        <Form.Item name="protocol" noStyle>
                          <Select
                            style={{ width: 90 }}
                            value={formState.protocol}
                            onChange={value => {
                              setFormState(prev => ({ ...prev, protocol: value }))
                            }}
                          >
                            {protocolOptions.map(option => (
                              <Select.Option key={option.value} value={option.value}>
                                {option.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      }
                      placeholder={t('请输入')}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item label="" className="form-item" style={{ marginLeft: 16 }}>
                    <Checkbox
                      checked={formState.enableAuth}
                      onChange={e => {
                        const checked = e.target.checked
                        setFormState(prev => ({ ...prev, enableAuth: checked }))

                        // 如果关闭账密验证，清除代理池字段的校验提示
                        if (!checked) {
                          createForm.setFields([
                            {
                              name: 'proxy',
                              errors: [],
                            },
                          ])
                        }
                      }}
                    >
                      {t('启用账密认证')}
                    </Checkbox>
                  </Form.Item>
                </div>
                <div className="auth" style={{ display: 'flex', marginLeft: 115 }}>
                  {formState.enableAuth && (
                    <Form.Item
                      label={<span style={{ color: '#666' }}>{t('账号')}</span>}
                      name="account"
                      className="auth-form-item"
                      rules={[{ required: true, message: t('请输入账号') }]}
                    >
                      <Input
                        placeholder={t('请输入')}
                        style={{ width: 255 }}
                        maxLength={200}
                        allowClear
                      />
                    </Form.Item>
                  )}
                </div>
                <div className="auth" style={{ display: 'flex', marginLeft: 115 }}>
                  {formState.enableAuth && (
                    <Form.Item
                      label={<span style={{ color: '#666' }}>{t('密码')}</span>}
                      name="password"
                      className="auth-form-item"
                      rules={[{ required: true, message: t('请输入密码') }]}
                    >
                      <Input
                        placeholder={t('请输入')}
                        style={{ width: 255 }}
                        maxLength={200}
                        allowClear
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            </div>

            {/* 报表发送配置 */}
            <div className="section">
              <div className="section-title">
                {t('报表发送配置')}
                <div className="section-bar" />
              </div>
              <div className="form-content">
                <div className="code" style={{ display: 'flex' }}>
                  <Form.Item
                    label={
                      <div className="custom-label">
                        <span>{t('发送报表')}</span>
                      </div>
                    }
                    valuePropName="checked"
                    name="send"
                    className="form-item"
                  >
                    <BasSwitch
                      onChange={checked => setFormState(prev => ({ ...prev, send: checked }))}
                      checked={formState.send}
                    />
                  </Form.Item>
                  {formState.send && (
                    <Form.Item
                      label={<span style={{ color: '#666' }}>{t('发送邮箱')}</span>}
                      name="emailList"
                      className="encoding-type-form-item"
                      rules={[{ required: true, message: t('请选择发送邮箱') }]}
                    >
                      <Select
                        placeholder={t('请选择发送邮箱')}
                        mode="multiple"
                        showArrow
                        allowClear
                        maxTagCount={1}
                        style={{ width: 300 }}
                      >
                        {emailList.map(item => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}/{item.email}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </div>
                {formState.send && (
                  <div className="code" style={{ display: 'flex' }}>
                    <Form.Item
                      label={
                        <div className="custom-label">
                          <span>{t('过滤配置')}</span>
                        </div>
                      }
                      valuePropName="checked"
                      name="filter"
                      className="form-item"
                    >
                      <BasSwitch
                        onChange={checked => setFormState(prev => ({ ...prev, filter: checked }))}
                        checked={formState.filter}
                      />
                    </Form.Item>
                    {formState.filter && (
                      <Form.Item
                        label={<span style={{ color: '#666' }}>{t('过滤条件')}</span>}
                        name="condition"
                        className="encoding-type-form-item"
                        rules={[{ required: true, message: t('请选择过滤条件') }]}
                      >
                        <Radio.Group>
                          {conditionOptions.map(option => (
                            <Radio key={option.value} value={option.value}>
                              {option.label}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Form>
        </ConfigContainer>
      )}

      {step === 2 && (
        <TemplateContainer>
          <Form form={form} onFinish={handleSubmit}>
            <div className="template-section">
              <div style={{ margin: '10px 0' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                  }}
                >
                  <div className="section-title">{t('模板选择')}</div>
                  <div style={{ marginRight: 16 }}>
                    <FilterFormSelect
                      filterOptions={templateSearchOptions}
                      value={templateValue}
                      onChange={newValue => {
                        setTemplateValue(newValue)
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="search-area">
                <Checkbox
                  checked={
                    selectedTemplateIds.length === templateIds.length && templateIds.length > 0
                  }
                  indeterminate={
                    selectedTemplateIds.length > 0 &&
                    selectedTemplateIds.length < templateIds.length
                  }
                  onChange={() => onSelectAll(selectedTemplateIds.length !== templateIds.length)}
                >
                  {t('全选所有')}
                </Checkbox>
              </div>
              <Table
                columns={templateColumns}
                dataSource={templateData.list}
                loading={templateLoading}
                rowSelection={{
                  ...templateRowSelection,
                  preserveSelectedRowKeys: true, // 添加这个属性来保持选中状态
                }}
                rowKey="templateId"
                pagination={{
                  total: templateData.total,
                  current: searchTemplateParams.page,
                  pageSize: searchTemplateParams.pageSize,
                  showSizeChanger: paginationConfig.showSizeChanger,
                  showTotal: paginationConfig.showTotal,
                }}
                onChange={onTemplateTableChange}
              />
            </div>

            <div className="case-section">
              <div className="header-row">
                <div className="section-title">
                  {t('已选用例 (')}
                  {selectedCaseIds.length})
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FilterFormSelect
                    filterOptions={options}
                    value={caseValue}
                    onChange={newValue => {
                      console.log('newValue', newValue)
                      setCaseValue(newValue)
                    }}
                  />

                  <Button
                    type="primary"
                    size="small"
                    className="save-template-btn"
                    onClick={() => setSaveTemplateModal(true)}
                    disabled={selectedCaseIds.length === 0}
                  >
                    {t('保存为模板')}
                  </Button>
                </div>
              </div>
              <div className="search-area">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={() => onCaseSelectAll(!isAllSelected)}
                >
                  {t('全选所有')}
                </Checkbox>
              </div>
              <Table
                className="case-table"
                key={caseKey}
                columns={selectedCaseColumns}
                rowSelection={{
                  ...caseRowSelection,
                  preserveSelectedRowKeys: true, // 添加这个属性来保持选中状态
                }}
                dataSource={caseData.list || []}
                loading={caseLoading}
                rowKey="resourceId"
                pagination={{
                  total: caseData.total,
                  current: searchCaseParams.page,
                  pageSize: searchCaseParams.limit,
                  showSizeChanger: paginationConfig.showSizeChanger,
                  showTotal: paginationConfig.showTotal,
                }}
                onChange={onCaseTableChange}
              />
            </div>
          </Form>
          <Modal
            title={<SignTitle show>{t('保存为模板')}</SignTitle>}
            visible={saveTemplateModal}
            onOk={() => {
              form.submit()
            }}
            onCancel={() => {
              form.resetFields()
              setSaveTemplateModal(false)
            }}
            confirmLoading={saveCaseToTemplateLoading}
            centered
          >
            <Form
              form={form}
              layout={'vertical'}
              onFinish={values =>
                onSaveTemplateSubmit(values)(
                  selectedCaseIds,
                  saveCaseToTemplate,
                  setSaveTemplateModal,
                )
              }
            >
              <Form.Item name="name" rules={validationRules.templateName} label={t('模板名称')}>
                <Input placeholder={t('请输入模板名称')} maxLength={99} />
              </Form.Item>
            </Form>
          </Modal>
          <CaseDetail
            visible={detailVisible.visible}
            onClose={() => setDetailVisible({ visible: false, resourceId: '' })}
            resourceId={detailVisible.resourceId}
            attackTypeList={attackTypeList}
          />
        </TemplateContainer>
      )}
      <FooterButtons
        step={step}
        handleBack={handleBack}
        submitHandler={submitHandler}
        createForm={createForm}
        selectedCaseIds={selectedCaseIds}
        createTaskLoading={createTaskLoading}
        formState={formState}
      />
    </Container>
  )
}
